import React, { useMemo } from 'react';
import { useSelectableList } from '@fullstory/react-hooks';
import { useStarredRecentResources } from '../../hooks';
import { IconName, Select } from '@fullstory/components';
import { useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';

type ResourceSelectOptionsItemType = 'resource';

type ResourceSelectOptionsProps<T> = {
  items: T[];
  starredItems?: T[];
  recentItems?: T[];
  recentItemsLimit?: number;
  selected?: T;
  onChange: (item: T) => void;
  getItemProps: (item: T) => {
    id: string;
    label: string;
    starred: boolean;
    lastViewed?: Date;
    iconName?: IconName;
  };
  /**
   * String that identifies the type of item being filtered. (e.g. "Metric")
   */
  itemType: ResourceSelectOptionsItemType;
  active: boolean;
};

const resourceSelectOptionsMessages: {
  [key in ResourceSelectOptionsItemType]: { groupLabel: string };
} = {
  resource: { groupLabel: intl.formatMessage({ defaultMessage: 'All resources', id: 'ad8AiS' }) },
};

export const ResourceSelectOptions = <T extends any>({
  items,
  starredItems,
  recentItems,
  recentItemsLimit,
  selected,
  onChange,
  active,
  getItemProps,
  itemType,
}: ResourceSelectOptionsProps<T>) => {
  const intl = useIntl();

  const { starred, recent, other } = useStarredRecentResources({
    items,
    starredItems,
    recentItems,
    recentItemsLimit,
    getItemProps,
    includeGroupField: false,
  });

  const groupedAndOrderedItems = useMemo(
    () => [...starred, ...recent, ...other],
    [starred, recent, other],
  );

  const { getListItemProps } = useSelectableList(
    groupedAndOrderedItems,
    (maybeOption, option) => getItemProps(maybeOption).id === getItemProps(option).id,
    onChange,
    { active },
  );

  const renderItemGroup = (label: string, items: T[]) =>
    items.length ? (
      <>
        <Select.OptionGroupHeader>{label}</Select.OptionGroupHeader>
        {items.map(item => {
          const { iconName, id, label } = getItemProps(item);
          return (
            <Select.Option
              key={id}
              {...getListItemProps(item)}
              selected={item === selected}
              leftIcon={iconName}
            >
              {label}
            </Select.Option>
          );
        })}
      </>
    ) : null;

  const messages = resourceSelectOptionsMessages[itemType];

  return (
    <Select.Options data-component="ResourceSelectOptions">
      {renderItemGroup(intl.formatMessage({ defaultMessage: 'Starred', id: 'V7cUPv' }), starred)}
      {renderItemGroup(intl.formatMessage({ defaultMessage: 'Recent', id: 'iXpep1' }), recent)}
      {renderItemGroup(messages.groupLabel, other)}
    </Select.Options>
  );
};
